import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Eye, EyeSlash, ArrowUpRight } from 'react-bootstrap-icons';
import AuthCardLeft from './AuthCardLeft';
import MobileDesktopOnlyModal from './MobileDesktopOnlyModal';
import PasswordField from '../common/PasswordField';
import api from '../../services/api';
import './Auth.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const fromDashboard = searchParams.get('fromDashboard') === 'true';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordRequirements = [
    { text: 'At least 6 characters', minLength: 6 },
    { text: 'At least 1 uppercase letter', regex: /[A-Z]/ },
    { text: 'At least 1 lowercase letter', regex: /[a-z]/ },
    { text: 'At least 1 number', regex: /\d/ }
  ];

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid or missing reset token');
        setValidating(false);
        return;
      }

      try {
        const response = await api.post<{ valid: boolean; email?: string; error?: string }>('/api/password-reset/validate-token', { token });
        if (response.valid) {
          setTokenValid(true);
        } else {
          setError('Invalid or expired reset token');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Invalid or expired reset token');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/password-reset/reset', {
        token,
        password,
      });

      setSuccess(true);

      setTimeout(() => {
        if (fromDashboard) {
          navigate('/dashboard', {
            state: { successMessage: 'Your profile has been updated.' }
          });
        } else {
          navigate('/signin', {
            state: { message: null }
          });
        }
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-container">
        <MobileDesktopOnlyModal />
        <Card className="auth-card">
          <Card.Body>
            <AuthCardLeft />
            <div className="auth-card-right">
              <div style={{ width: '100%', maxWidth: '400px' }}>
                <div className="text-center">
                  <Spinner animation="border" role="status" className="mb-3" />
                  <h2>Validating reset token...</h2>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container">
        <MobileDesktopOnlyModal />
        <Card className="auth-card">
          <Card.Body>
            <AuthCardLeft />
            <div className="auth-card-right">
              <div style={{ width: '100%', maxWidth: '400px' }}>
                <h1 className="text-center">Invalid Reset Link</h1>
                <Alert variant="danger" className="mt-3">
                  <p>{error}</p>
                  <p>The password reset link may have expired or already been used.</p>
                </Alert>
                <Button
                  variant="primary"
                  onClick={() => navigate('/forgot-password')}
                  className="w-100 mt-3"
                >
                  REQUEST NEW RESET LINK
                </Button>
                <div className="w-100 text-center mt-2">
                  <h2>Remember your password? &nbsp;<Link to="/signin">SIGN IN <ArrowUpRight className="me-2" /></Link></h2>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <MobileDesktopOnlyModal />
        <Card className="auth-card">
          <Card.Body>
            <AuthCardLeft />
            <div className="auth-card-right">
              <div style={{ width: '100%', maxWidth: '400px' }}>
                <h1 className="text-center">Password Reset Successful!</h1>
                <Alert variant="success" className="mt-3">
                  <p>Your password has been reset successfully.</p>
                  <p>{fromDashboard ? 'Redirecting to your dashboard...' : 'Redirecting to sign in page...'}</p>
                </Alert>
                <div className="text-center">
                  <Spinner animation="border" size="sm" className="mt-2" />
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <MobileDesktopOnlyModal />
      <Card className="auth-card">
        <Card.Body>
          <AuthCardLeft />
          <div className="auth-card-right">
            <div style={{ width: '100%', maxWidth: '400px' }}>
              <h1 className="text-center">Reset Your Password</h1>
              <h2 className="text-center mb-4">Enter your new password</h2>

              {error && <div className="text-danger text-center mb-3">{error}</div>}

              <Form onSubmit={handleSubmit} noValidate>
                <PasswordField
                  label="Enter New Password"
                  value={password}
                  onChange={setPassword}
                  placeholder="Create a secure password"
                  disabled={loading}
                  requirements={passwordRequirements}
                  strengthLengthBonus={10}
                />

                <Form.Group className="mb-4">
                  <Form.Label>Confirm New Password</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Must match password above"
                      required
                      disabled={loading}
                      minLength={6}
                      style={{ paddingRight: '45px' }}
                    />
                    <Button
                      variant="link"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                      type="button"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        padding: '5px',
                        color: 'var(--color-text-secondary)',
                        zIndex: 10
                      }}
                    >
                      {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                </Form.Group>

                <Button
                  disabled={loading || !password || !confirmPassword}
                  className="w-100"
                  type="submit"
                >
                  {loading ? 'SETTING NEW PASSWORD...' : 'SET NEW PASSWORD'}
                </Button>
              </Form>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ResetPassword;